import axios from 'axios'
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ADD_USER_FAIL,
  ADD_USER_REQUEST,
  ADD_USER_RESET,
  ADD_USER_SUCCESS,
  GET_ROLE_FAIL,
  GET_ROLE_REQUEST,
  GET_ROLE_RESET,
  GET_ROLE_SUCCESS,
  GET_ADMINS_FAIL,
  GET_ADMINS_REQUEST,
  GET_ADMINS_RESET,
  GET_ADMINS_SUCCESS,
  USER_LOGIN_FAIL,
  USER_LOGIN_REQUEST,
  USER_LOGIN_SUCCESS,
  USER_LOGOUT,
  USER_LOGOUT_REQUEST,
  USER_LOGOUT_SUCCESS,
  USER_LOGOUT_FAIL,
  GET_ADMIN_BY_ID_FAIL,
  GET_ADMIN_BY_ID_REQUEST,
  GET_ADMIN_BY_ID_RESET,
  GET_ADMIN_BY_ID_SUCCESS,
  ADD_CUSTOMER_TO_USER_FAIL,
  ADD_CUSTOMER_TO_USER_REQUEST,
  ADD_CUSTOMER_TO_USER_RESET,
  ADD_CUSTOMER_TO_USER_SUCCESS,
  REMOVE_CUSTOMER_FROM_USER_FAIL,
  REMOVE_CUSTOMER_FROM_USER_REQUEST,
  REMOVE_CUSTOMER_FROM_USER_RESET,
  REMOVE_CUSTOMER_FROM_USER_SUCCESS,
  RESET_DEVICE_ID_FAIL,
  RESET_DEVICE_ID_REQUEST,
  RESET_DEVICE_ID_RESET,
  RESET_DEVICE_ID_SUCCESS,
  RESET_PASSWORD_FAIL,
  RESET_PASSWORD_REQUEST,
  RESET_PASSWORD_RESET,
  RESET_PASSWORD_SUCCESS,
  UPDATE_USER_FAIL,
  UPDATE_USER_REQUEST,
  UPDATE_USER_RESET,
  UPDATE_USER_SUCCESS,
  FETCH_ATTENDANCE_FAIL,
  FETCH_ATTENDANCE_REQUEST,
  FETCH_ATTENDANCE_RESET,
  FETCH_ATTENDANCE_SUCCESS,
  RESIGN_USER_FAIL,
  RESIGN_USER_REQUEST,
  RESIGN_USER_RESET,
  RESIGN_USER_SUCCESS,
  FORCE_LOGOUT_USER_FAIL,
  FORCE_LOGOUT_USER_REQUEST,
  FORCE_LOGOUT_USER_RESET,
  FORCE_LOGOUT_USER_SUCCESS,
  FORCE_LOGOUT_MULTIPLE_FAIL,
  FORCE_LOGOUT_MULTIPLE_REQUEST,
  FORCE_LOGOUT_MULTIPLE_RESET,
  FORCE_LOGOUT_MULTIPLE_SUCCESS,
  DELETE_ADMIN_REQUEST,
  DELETE_ADMIN_SUCCESS,
  DELETE_ADMIN_FAIL,
  BULK_DELETE_ADMINS_REQUEST,
  BULK_DELETE_ADMINS_SUCCESS,
  BULK_DELETE_ADMINS_FAIL,
} from '../constants/adminConstant.js';
import {
  clearStoredAuthSessions,
  getStoredAuthSessions,
  invalidateServerSessions,
  suppressToastsAfterLogout,
} from '../utils/authLogout.js';

const baseUrl = process.env.REACT_APP_API_URL;
const roleRequestInFlight = new Map();

export const login = (email, password) => async (dispatch) => {
  try {
    dispatch({
      type: USER_LOGIN_REQUEST,
    })

    const config = {
      headers: {
        'Content-Type': 'application/json',
      },
    }

    const { data } = await axios.post(
      `${baseUrl}/admins/login`,
      { email, password },
      config
    )

    dispatch({
      type: USER_LOGIN_SUCCESS,
      payload: data,
    })

    localStorage.setItem('adminInfo', JSON.stringify(data))
  } catch (error) {
    dispatch({
      type: USER_LOGIN_FAIL,
      payload:
        error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : error.message || 'Login failed',
    })
  }
}

export const logout = () => async (dispatch) => {
  const sessions = getStoredAuthSessions()

  dispatch({
    type: USER_LOGOUT_REQUEST,
  })

  suppressToastsAfterLogout()
  clearStoredAuthSessions()
  dispatch({
    type: USER_LOGOUT,
  })

  try {
    await invalidateServerSessions(baseUrl, sessions)

    dispatch({
      type: USER_LOGOUT_SUCCESS,
      payload: { ok: true },
    })
  } catch (error) {
    dispatch({
      type: USER_LOGOUT_FAIL,
      payload: error.response?.data?.message || error.message,
    })
  }
}

export const getRole = () => async (dispatch, getState) => {
  const {
    adminLogin: { userInfo: adminInfo },
  } = getState()

  // Safety check - if no adminInfo or token, don't proceed
  if (!adminInfo || !adminInfo.token) {
    console.warn('getRole: No admin info or token found')
    dispatch({
      type: GET_ROLE_FAIL,
      payload: 'Not authenticated',
    })
    return
  }

  const roleId = adminInfo.role?._id || adminInfo.role
  if (!roleId) {
    dispatch({
      type: GET_ROLE_FAIL,
      payload: 'Role not found',
    })
    return
  }

  const requestKey = `${adminInfo.token}:${roleId.toString()}`
  const existingRequest = roleRequestInFlight.get(requestKey)
  if (existingRequest) {
    return existingRequest
  }

  dispatch({
    type: GET_ROLE_REQUEST,
  })

  const requestPromise = (async () => {
    try {
      const config = {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${adminInfo.token}`,
        },
      }

      const { data } = await axios.get(`${baseUrl}/roles/${roleId}`, config)

      // Guard against malformed/unexpected responses (e.g. an API middleware
      // short-circuiting with `[]`) so a bad response can't wipe out the
      // admin's real role/permissions in Redux or localStorage.
      const isValidRole = data && typeof data === 'object' && !Array.isArray(data) && data._id

      if (!isValidRole) {
        dispatch({
          type: GET_ROLE_FAIL,
          payload: 'Received an invalid role response; keeping existing role',
        })
        return null
      }

      dispatch({
        type: GET_ROLE_SUCCESS,
        payload: data,
      })

      // Update adminInfo in localStorage with fresh role data
      const updatedAdminInfo = {
        ...adminInfo,
        role: data
      }
      localStorage.setItem('adminInfo', JSON.stringify(updatedAdminInfo))

      return data
    } catch (error) {
      dispatch({
        type: GET_ROLE_FAIL,
        payload:
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : error.message || 'Failed to get role',
      })
      return null
    } finally {
      roleRequestInFlight.delete(requestKey)
    }
  })()

  roleRequestInFlight.set(requestKey, requestPromise)
  return requestPromise
}

export const getUsers = (search = '') => async (dispatch, getState) => {
  try {
    dispatch({
      type: GET_ADMINS_REQUEST,
    })

    const {
      adminLogin: { userInfo: adminInfo },
    } = getState()

    const config = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${adminInfo.token}`,
      },
    }

    const url = search ? `${baseUrl}/admins?search=${encodeURIComponent(search)}` : `${baseUrl}/admins`
    const { data } = await axios.get(url, config)

    dispatch({
      type: GET_ADMINS_SUCCESS,
      payload: data,
    })
  } catch (error) {
    dispatch({
      type: GET_ADMINS_FAIL,
      payload:
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message,
    })
  }
}

export const addUser =
  (name, email, role, assignedClubs) =>
    async (dispatch, getState) => {
      try {
        dispatch({
          type: ADD_USER_REQUEST,
        })

        const {
          adminLogin: { userInfo: adminInfo },
        } = getState()

        const config = {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${adminInfo.token}`,
          },
        }

        const { data } = await axios.post(
          `${baseUrl}/admins`,
          { name, email, role, assignedClubs },
          config
        )

        dispatch({
          type: ADD_USER_SUCCESS,
          payload: data,
        })
      } catch (error) {
        dispatch({
          type: ADD_USER_FAIL,
          payload:
            error.response && error.response.data.message
              ? error.response.data.message
              : error.message,
        })
      }
    }

export const getAdminByID = (id) => async (dispatch, getState) => {
  try {
    dispatch({
      type: GET_ADMIN_BY_ID_REQUEST,
    })

    const {
      adminLogin: { userInfo: adminInfo },
    } = getState()

    const config = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${adminInfo.token}`,
      },
    }

    const { data } = await axios.get(`${baseUrl}/admins/${id}`, config)

    dispatch({
      type: GET_ADMIN_BY_ID_SUCCESS,
      payload: data,
    })
  } catch (error) {
    dispatch({
      type: GET_ADMIN_BY_ID_FAIL,
      payload:
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message,
    })
  }
}

export const updateUser = (id, name, role, email, assignedClubs) =>
  async (dispatch, getState) => {
    try {
      dispatch({
        type: UPDATE_USER_REQUEST,
      })

      const {
        adminLogin: { userInfo: adminInfo },
      } = getState()

      const config = {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${adminInfo.token}`,
        },
      }

      const body = { name, role, email }
      if (assignedClubs !== undefined) {
        body.assignedClubs = assignedClubs
      }

      const { data } = await axios.put(
        `${baseUrl}/admins/${id}`,
        body,
        config
      )

      dispatch({
        type: UPDATE_USER_SUCCESS,
        payload: data,
      })
    } catch (error) {
      dispatch({
        type: UPDATE_USER_FAIL,
        payload:
          error.response && error.response.data.message
            ? error.response.data.message
            : error.message,
      })
    }
  }

export const resetDeviceID = (id) => async (dispatch, getState) => {
  try {
    dispatch({
      type: RESET_DEVICE_ID_REQUEST,
    })

    const {
      adminLogin: { userInfo: adminInfo },
    } = getState()

    const config = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${adminInfo.token}`,
      },
    }

    const { data } = await axios.put(
      `${baseUrl}/admins/reset-device-id/${id}`,
      {},
      config
    )

    dispatch({
      type: RESET_DEVICE_ID_SUCCESS,
      payload: data,
    })
  } catch (error) {
    dispatch({
      type: RESET_DEVICE_ID_FAIL,
      payload:
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message,
    })
  }
}

export const resetPassword = (id, password = null) => async (dispatch, getState) => {
  try {
    dispatch({
      type: RESET_PASSWORD_REQUEST,
    })

    const {
      adminLogin: { userInfo: adminInfo },
    } = getState()

    const config = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${adminInfo.token}`,
      },
    }

    const body = password ? { password } : {}

    const { data } = await axios.put(
      `${baseUrl}/admins/reset-password/${id}`,
      body,
      config
    )

    dispatch({
      type: RESET_PASSWORD_SUCCESS,
      payload: data,
    })
  } catch (error) {
    dispatch({
      type: RESET_PASSWORD_FAIL,
      payload:
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message,
    })
  }
}

export const fetchAttendance = (id, date) => async (dispatch, getState) => {
  try {
    dispatch({
      type: FETCH_ATTENDANCE_REQUEST,
    })

    const {
      adminLogin: { userInfo: adminInfo },
    } = getState()

    const config = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${adminInfo.token}`,
      },
    }

    const { data } = await axios.get(
      `${baseUrl}/admins/fetch-attendance/${id}/${date}`,
      config
    )

    dispatch({
      type: FETCH_ATTENDANCE_SUCCESS,
      payload: data,
    })
  } catch (error) {
    dispatch({
      type: FETCH_ATTENDANCE_FAIL,
      payload:
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message,
    })
  }
}

export const resignUser = (id) => async (dispatch, getState) => {
  try {
    dispatch({
      type: RESIGN_USER_REQUEST,
    })

    const {
      adminLogin: { userInfo: adminInfo },
    } = getState()

    const config = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${adminInfo.token}`,
      },
    }

    const { data } = await axios.put(`${baseUrl}/admins/resign/${id}`, {}, config)

    dispatch({
      type: RESIGN_USER_SUCCESS,
      payload: data,
    })
  } catch (error) {
    dispatch({
      type: RESIGN_USER_FAIL,
      payload:
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message,
    })
  }
}

export const forceLogoutUser = (id) => async (dispatch, getState) => {
  try {
    dispatch({
      type: FORCE_LOGOUT_USER_REQUEST,
    })

    const {
      adminLogin: { userInfo: adminInfo },
    } = getState()

    const config = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${adminInfo.token}`,
      },
    }

    const { data } = await axios.put(`${baseUrl}/admins/force-logout/${id}`, {}, config)

    dispatch({
      type: FORCE_LOGOUT_USER_SUCCESS,
      payload: data,
    })
  } catch (error) {
    dispatch({
      type: FORCE_LOGOUT_USER_FAIL,
      payload:
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message,
    })
  }
}

export const forceLogoutMultipleUsers = (userIds) => async (dispatch, getState) => {
  try {
    dispatch({
      type: FORCE_LOGOUT_MULTIPLE_REQUEST,
    })

    const {
      adminLogin: { userInfo: adminInfo },
    } = getState()

    const config = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${adminInfo.token}`,
      },
    }

    const { data } = await axios.put(`${baseUrl}/admins/force-logout-multiple`, { userIds }, config)

    dispatch({
      type: FORCE_LOGOUT_MULTIPLE_SUCCESS,
      payload: data,
    })
  } catch (error) {
    dispatch({
      type: FORCE_LOGOUT_MULTIPLE_FAIL,
      payload:
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message,
    })
  }
}

export const deleteAdmin = (adminId) => async (dispatch, getState) => {
  try {
    dispatch({
      type: DELETE_ADMIN_REQUEST,
    })

    const {
      adminLogin: { userInfo: adminInfo },
    } = getState()

    const config = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${adminInfo.token}`,
      },
    }

    const { data } = await axios.delete(`${baseUrl}/admins/${adminId}`, config)

    dispatch({
      type: DELETE_ADMIN_SUCCESS,
      payload: data,
    })

    return data
  } catch (error) {
    dispatch({
      type: DELETE_ADMIN_FAIL,
      payload:
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message,
    })
    throw error
  }
}

export const bulkDeleteAdmins = (userIds) => async (dispatch, getState) => {
  try {
    dispatch({
      type: BULK_DELETE_ADMINS_REQUEST,
    })

    const {
      adminLogin: { userInfo: adminInfo },
    } = getState()

    const config = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${adminInfo.token}`,
      },
    }

    const { data } = await axios.post(`${baseUrl}/admins/bulk-delete`, { userIds }, config)

    dispatch({
      type: BULK_DELETE_ADMINS_SUCCESS,
      payload: data,
    })

    return data
  } catch (error) {
    dispatch({
      type: BULK_DELETE_ADMINS_FAIL,
      payload:
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message,
    })
    throw error
  }
}
